import { PropsWithChildren } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { globalTranslationKeys, travelerModalTranslationKeys } from 'obt-common';
import Box from '@spotnana/pixel-react/dist/Box';
import CircularLoader from '@spotnana/pixel-react/dist/CircularLoader';
import Grid from '@spotnana/pixel-react/dist/Grid';
import * as Icons from '@spotnana/pixel-react/dist/Icons';
import Typography from '@spotnana/pixel-react/dist/Typography';
import spotnanaTheme from '@spotnana/pixel-react/dist/utils/themes/theme';
import { Modal } from '@spotnana/blocks/src/Modal';
import { Button } from '@spotnana/blocks/src/Button';

interface IProps {
  isNewEntry: boolean;
  onSave: () => void;
  onDelete?: () => void;
  deleteButtonLabel?: string;
  onClose: () => void;
  children: React.ReactNode;
  title: string;
  subTitle?: string;
  dataTestId?: string;
  nodeBetweenActions?: React.ReactNode;
  isLoadingOrError?: boolean | 'error';
  loadingMessage?: string;
  isSaving?: boolean;
  isDisableSave?: boolean;
  noBackDrop?: boolean;
  allowVerticalOverflow?: boolean;
}

interface HeaderProps {
  title: string;
  subTitle?: string;
  isNewEntry: boolean;
}

const Header = ({ title, isNewEntry, subTitle }: HeaderProps): JSX.Element => {
  const { t } = useTranslation('PROFILE');
  const { ADD, EDIT, REQUIRED_FIELDS } = travelerModalTranslationKeys;
  return (
    <Box display="flex" width="full" flexDirection="column" justifyContent="space-between">
      <Typography variation="h3" fontWeight="regular">
        {isNewEntry ? `${t(ADD)} ${title}` : `${t(EDIT)} ${title}`}
      </Typography>
      <Box mt={3}>
        <Typography variation="small">{subTitle ?? t(REQUIRED_FIELDS)}</Typography>
      </Box>
    </Box>
  );
};

const ProfileModal = ({
  isNewEntry,
  onSave,
  onDelete,
  deleteButtonLabel,
  onClose,
  title,
  subTitle,
  children,
  dataTestId,
  nodeBetweenActions,
  isLoadingOrError = false,
  loadingMessage,
  isSaving,
  isDisableSave = false,
  allowVerticalOverflow = false,
}: PropsWithChildren<IProps>): JSX.Element => {
  const { t } = useTranslation('PROFILE');
  const globalTranslation = useTranslation();
  const { DELETE, SAVE, UPDATE, SAVING, UPDATING } = travelerModalTranslationKeys;
  const { UNABLE_TO_LOAD } = globalTranslationKeys;

  const disableSaveButton = isDisableSave || isLoadingOrError !== false;

  return (
    <Modal
      open
      onClose={onClose}
      header={{ content: <Header title={title} subTitle={subTitle} isNewEntry={isNewEntry} /> }}
      data-testId={`modal_${dataTestId}`}
    >
      <Box overflowY={allowVerticalOverflow ? 'visible' : 'auto'} overflowX="hidden">
        <Box position="relative">
          {children}

          <Box mt={20}>
            <Grid.Row between="xs">
              <Grid.Col>
                {/* TODO (Piyush) - Remove dependency on isNewEntry */}
                {!isNewEntry && onDelete && (
                  <Box width={152} height={56}>
                    <Button variant="outline" onClick={onDelete} data-testid="btn_form-delete" fullWidth>
                      <Typography variation="body" fontWeight="600">
                        {deleteButtonLabel ?? t(DELETE)}
                      </Typography>
                    </Button>
                  </Box>
                )}
              </Grid.Col>
              <Grid.Col>{nodeBetweenActions}</Grid.Col>
              <Grid.Col>
                <Box width={152} height={56}>
                  <Button
                    variant="primary"
                    id="profile-submit-button"
                    loading={isSaving}
                    onClick={onSave}
                    data-testid="btn_form-submit"
                    disabled={disableSaveButton}
                    fullWidth
                  >
                    {isNewEntry ? <>{isSaving ? t(SAVING) : t(SAVE)}</> : <>{isSaving ? t(UPDATING) : t(UPDATE)}</>}
                  </Button>
                </Box>
              </Grid.Col>
            </Grid.Row>
          </Box>

          {isLoadingOrError && (
            <Box
              position="absolute"
              top="0"
              height="full"
              width="full"
              display="flex"
              justifyContent="center"
              alignItems="center"
              background={`radial-gradient(circle at center, ${spotnanaTheme.colors.bg.white}, transparent)`}
            >
              {isLoadingOrError === 'error' ? (
                <Box display="flex" alignItems="center">
                  <Icons.Warning width={32} height={32} primaryColor={spotnanaTheme.colors.primaryEmphasis} />
                  <Box mx={12}>
                    <Typography variation="body">{globalTranslation.t(UNABLE_TO_LOAD)}</Typography>
                  </Box>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" flexDirection="column">
                  <CircularLoader size={48} />
                  {loadingMessage && (
                    <Box mt={24}>
                      <Typography variation="small">{loadingMessage}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ProfileModal;
